import { useCurrency } from '../context/CurrencyContext';
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle, FileText, Gift } from 'lucide-react';
import api from '../api/client';

export default function SignAgreementPage() {
  const { agreementId } = useParams();
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signed, setSigned] = useState(false);
  const [signing, setSigning] = useState(false);
  const [form, setForm] = useState({ name: '', email: '' });

  useEffect(() => {
    api.get(`/api/config/agreements/${agreementId}/details`)
      .then(res => {
        setAgreement(res.data);
        if (res.data.merchantSignedAt) setSigned(true);
      })
      .catch(() => toast.error('Agreement not found'))
      .finally(() => setLoading(false));
  }, [agreementId]);

  const handleSign = async (e) => {
    e.preventDefault();
    setSigning(true);
    try {
      await api.post(`/api/config/agreements/${agreementId}/merchant-sign`, form);
      toast.success('Agreement signed successfully!');
      setSigned(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to sign');
    } finally { setSigning(false); }
  };

  const { formatShortDate: formatDate } = useCurrency();

  if (loading) return (
    <div className="min-h-screen bg-[#FFFCF9] flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[#561D1A] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!agreement) return (
    <div className="min-h-screen bg-[#FFFCF9] flex items-center justify-center text-gray-500">
      Agreement not found or has been removed.
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FFFCF9]" style={{ fontFamily: 'Roboto, sans-serif' }}>
      {/* Header */}
      <div className="bg-[#561D1A] text-white py-6">
        <div className="max-w-3xl mx-auto px-6 flex items-center gap-3">
          <div className="w-10 h-10 bg-[#EC1A23] rounded-xl flex items-center justify-center">
            <Gift size={22} className="text-white" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold">LuxeMall</h1>
            <p className="text-white/60 text-xs">Gift Voucher Program Agreement</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8">
        {signed ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <CheckCircle size={64} className="text-emerald-500 mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold text-[#561D1A] mb-2">Agreement Signed!</h2>
            <p className="text-gray-500 mb-2">Thank you for joining the LuxeMall Gift Voucher Program.</p>
            <p className="text-xs text-gray-400">Agreement No: {agreement.agreementNumber}</p>
          </div>
        ) : (
          <>
            {/* Agreement Document */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6" data-testid="agreement-document">
              <div className="bg-[#561D1A] px-6 py-4 text-center">
                <h2 className="text-[#D4AF37] font-heading text-xl font-bold">GIFT VOUCHER AGREEMENT</h2>
                <p className="text-white/60 text-xs mt-1">Mall Management & Merchant Agreement</p>
              </div>

              <div className="px-6 py-5">
                <div className="flex justify-between text-xs text-gray-500 mb-6">
                  <span>Agreement No: <strong className="text-gray-700 font-mono">{agreement.agreementNumber}</strong></span>
                  <span>Date: {formatDate(agreement.agreementStartDate)}</span>
                </div>

                {/* Parties */}
                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Mall Management (First Party)</p>
                    <p className="text-sm font-medium text-gray-700">LuxeMall Management</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Merchant / Store (Second Party)</p>
                    <p className="text-sm font-medium text-gray-700">{agreement.merchantName}</p>
                    {agreement.storeCategory && <p className="text-xs text-gray-500">{agreement.storeCategory}</p>}
                  </div>
                </div>

                {/* Details */}
                <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl p-4 mb-6">
                  <h4 className="text-xs text-[#D4AF37] uppercase tracking-wider font-bold mb-3">Agreement Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-gray-500">Commission:</span> <strong>{agreement.agreedPercentage}%</strong></div>
                    <div><span className="text-gray-500">Duration:</span> <strong>{agreement.validityPeriodMonths} month(s)</strong></div>
                    <div><span className="text-gray-500">Start:</span> <strong>{formatDate(agreement.agreementStartDate)}</strong></div>
                    <div><span className="text-gray-500">End:</span> <strong>{formatDate(agreement.agreementEndDate)}</strong></div>
                  </div>
                </div>

                {/* Terms */}
                <div className="space-y-4 text-sm text-gray-600 leading-relaxed">
                  <h3 className="font-bold text-[#561D1A] text-base">Terms and Conditions</h3>
                  {[
                    { title: '1. Scope', text: 'This agreement establishes the terms under which the Merchant participates in the Gift Voucher program managed by the Mall Management.' },
                    { title: '2. Commission & Payment', text: `The Merchant agrees to honor gift vouchers and accepts the ${agreement.agreedPercentage}% commission. Mall Management shall settle payments within 30 business days of redemption.` },
                    { title: '3. Voucher Acceptance', text: 'The Merchant shall accept all valid, unexpired gift vouchers and verify each voucher through the provided system.' },
                    { title: '4. Merchant Obligations', text: 'a) Maintain active store presence. b) Train staff on voucher verification. c) No additional fees to voucher customers. d) Maintain transaction records.' },
                    { title: '5. Mall Obligations', text: 'a) Provide verification systems and tools. b) Ensure timely payment settlements. c) Promote the gift voucher program. d) Provide support and training.' },
                    { title: '6. Duration', text: `Valid for ${agreement.validityPeriodMonths} months. Renewal subject to mutual agreement. Either party may terminate with 30 days written notice.` },
                    { title: '7. Termination', text: 'Either party may terminate with 30 days notice. Immediate termination for fraud or breach. Pending settlements completed within 30 days.' },
                    { title: '8. Confidentiality', text: 'Both parties maintain confidentiality of business information including commission rates, transaction volumes, and customer data.' },
                    { title: '9. Dispute Resolution', text: 'Disputes resolved first through mutual discussion, then mediation before legal proceedings.' },
                    { title: '10. General', text: 'This agreement constitutes the entire understanding. Amendments must be in writing and signed by both parties.' },
                  ].map(item => (
                    <div key={item.title}>
                      <h4 className="font-medium text-gray-800">{item.title}</h4>
                      <p className="text-gray-500 text-xs mt-1">{item.text}</p>
                    </div>
                  ))}
                </div>

                {/* Signature status */}
                <div className="mt-8 pt-6 border-t border-gray-100">
                  <h3 className="font-bold text-[#561D1A] mb-4">Signatures</h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center">
                      <p className="text-xs text-gray-400 uppercase mb-2">Mall Management</p>
                      {agreement.mallSignedAt ? (
                        <div className="text-emerald-600 text-sm font-medium flex items-center justify-center gap-1">
                          <CheckCircle size={14} /> Signed on {formatDate(agreement.mallSignedAt)}
                        </div>
                      ) : (
                        <p className="text-gray-300 text-sm">Pending</p>
                      )}
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-400 uppercase mb-2">Merchant / Store</p>
                      <p className="text-gray-300 text-sm">Awaiting your signature below</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sign Form */}
            <div className="bg-white rounded-2xl border-2 border-[#D4AF37]/30 shadow-sm p-6" data-testid="sign-form">
              <h3 className="font-heading text-lg font-bold text-[#561D1A] mb-1">Sign This Agreement</h3>
              <p className="text-xs text-gray-500 mb-5">By signing, you agree to all terms and conditions above.</p>
              <form onSubmit={handleSign} className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Your Full Name *</label>
                  <input required value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                    placeholder="e.g. John Kamau"
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="signer-name-input" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Your Email *</label>
                  <input required type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))}
                    placeholder="you@store.com"
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="signer-email-input" />
                </div>
                <button type="submit" disabled={signing}
                  className="w-full bg-[#EC1A23] hover:bg-[#C4121A] disabled:opacity-60 text-white py-3 rounded-full text-sm font-bold flex items-center justify-center gap-2" data-testid="sign-agreement-btn">
                  {signing ? 'Signing...' : <><FileText size={15} /> Sign Agreement</>}
                </button>
              </form>
            </div>

            <p className="text-center text-xs text-gray-400 mt-4">
              System-generated document | {agreement.agreementNumber}
            </p>
          </>
        )}

        <p className="text-center mt-8">
          <Link to="/" className="text-[#561D1A] text-sm hover:underline">Visit LuxeMall</Link>
        </p>
      </div>
    </div>
  );
}
